import {
  ConfirmationModal,
  LoadingSpinner,
  Table,
  Tooltip,
  EmployeeOrgChart,
  OrgTree,
  GET_EMPLOYEES_CONNECTION,
  UPDATE_EMPLOYEE,
  DELETE_EMPLOYEE,
  useCurrentCompany,
  useCompanyFeature,
  Button,
} from '@employ-engage/common-components';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { useEffect, useState } from 'react';
import {
  ActiveTableFilterOption,
  ActiveTableToggle,
  BasicTableDataRow,
  Company,
  departmentListToString,
  departmentStringToFriendlyString,
  employeeIsPartOfOrganization,
  Employee,
  NextLevelKey,
  OrgTreeItem,
  TableCellContentAlignEnum,
  TableFilter,
  TableSortOption,
  TableToggle,
  ManagementRole,
  CompanyFeaturesEnum,
  ContactNumber,
} from '@employ-engage/data';
import AddEmployeeModal from '../add-employee-modal/add-employee-modal';
import PhoneNumbersNotice from './phone-numbers-notice';
import classNames from 'classnames';
import { addMonths, isBefore } from 'date-fns';
import isDate from 'lodash.isdate';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';

/* eslint-disable-next-line */
export interface EmployeesProps {}

export function Employees(props: EmployeesProps) {
  const { t } = useTranslation(['common']);

  /**
   * Every number, the main one first (the first that gets texts, as on the
   * API). All of them are shown, not tucked behind a hover tooltip: touch
   * screens have no hover, and a count is no use to someone looking for a
   * number.
   */
  const phoneCell = (numbers: ContactNumber[]) => {
    if (!numbers.length) return '';
    const display = (n: string) => parsePhoneNumberFromString(n)?.formatNational() ?? n;
    const main = numbers.find((n) => n.receivesTexts) ?? numbers[0];
    const ordered = [main, ...numbers.filter((n) => n !== main)];
    if (ordered.length === 1) return display(main.number);
    return (
      <ul>
        {ordered.map((n) => (
          <li key={n.id}>{display(n.number)}</li>
        ))}
      </ul>
    );
  };
  const [isAddEmployeeModalOpen, setIsAddEmployeeModalOpen] = useState(false);
  const [employeeSelectedForArchiving, setEmployeeSelectedForArchiving] =
    useState<BasicTableDataRow | null>(null);
  const [employeeSelectedForDeletion, setEmployeeSelectedForDeletion] =
    useState<BasicTableDataRow | null>(null);
  const [
    isDeleteEmployeeConfirmationModalOpen,
    setIsDeleteEmployeeConfirmationModalOpen,
  ] = useState(false);
  const [
    isArchiveEmployeeConfirmationModalOpen,
    setIsArchiveEmployeeConfirmationModalOpen,
  ] = useState(false);
  const [view] = useState<'table' | 'tree-list'>('table');
  const [employeeSelectedForEditing, setEmployeeSelectedForEditing] =
    useState<BasicTableDataRow | null>(null);

  const [nextLevelKeys, setNextLevelKeys] = useState<NextLevelKey[]>([]);
  const [managementRoles, setManagementRoles] = useState<ManagementRole[]>([]);
  const {
    currentCompany: company,
    loading: isCompanyLoading,
    refetchCompany,
  } = useCurrentCompany();
  const { feature: multiplePhoneNumbersFeature } = useCompanyFeature(
    CompanyFeaturesEnum.MULTIPLE_PHONE_NUMBERS
  );
  const multiplePhoneNumbers = !!multiplePhoneNumbersFeature?.isEnabled;
  const {
    data: employeeData,
    loading: isEmployeesLoading,
    refetch: refetchEmployees,
  } = useQuery(GET_EMPLOYEES_CONNECTION, {
    variables: { first: 1000 },
  });
  const [updateEmployee] = useMutation(UPDATE_EMPLOYEE);
  const [deleteEmployee] = useMutation(DELETE_EMPLOYEE);

  useEffect(() => {
    if (company) {
      setNextLevelKeys(company.orgTreeNextLevelKeys || []);
      setManagementRoles(
        company.managementRoles || []
      );
      refetchEmployees();
    }
  }, [company]);

  const employees =
    employeeData?.employeesConnection?.edges.map((edge: any) => edge.node) ||
    [];

  const onConfirmArchiveEmployee = async () => {
    try {
      await updateEmployee({
        variables: {
          id: employeeSelectedForArchiving?.id,
          input: {
            archivedDate: new Date().toISOString(),
          },
        },
      });

      refetchEmployees();
      setIsArchiveEmployeeConfirmationModalOpen(false);
    } catch (error) {
      console.error('Error updating employee: ', error);
    }
  };

  const onConfirmDeleteEmployee = async () => {
    try {
      await deleteEmployee({
        variables: {
          id: employeeSelectedForDeletion?.id,
        },
      });

      refetchEmployees();
      setIsDeleteEmployeeConfirmationModalOpen(false);
    } catch (error) {
      console.error('Error deleting employee: ', error);
    }
  };

  const filters: TableFilter[] = [
    {
      id: 'departments',
      value: t('Departments'),
      options: [],
      isCustomFilter: true,
      panelCss: 'w-fit max-w-[500px] overflow-x-auto max-h-[50vh]',
    },
    {
      id: 'managementRoles',
      value: t('Roles'),
      panelCss: 'w-fit',
      options: [
        {
          key: 'none',
          value: 'none',
          label: t('(None)'),
          filter: (employee: any) => {
            return employee.managementRoles.length === 0;
          },
        },
        ...managementRoles.map((managementRole, index) => {
          return {
            key: managementRole.id + index,
            value: managementRole.name,
            label: managementRole.name,
            filter: (employee: any) => {
              return employee.managementRoles
                .map((role: ManagementRole) => role.name)
                .includes(managementRole.name);
            },
          };
        }),
      ],
    },
  ];

  const [activeFilters, setActiveFilters] = useState<ActiveTableFilterOption[]>(
    []
  );

  const toggles: TableToggle[] = [
    {
      id: 'show-archived',
      label: t('Show Archived'),
      enabledByDefault: true,
      filter: (_: any) => true,
      negativeFilter: (row: any) => !row.archivedDate,
    },
  ];

  const [activeToggles, setActiveToggles] = useState<ActiveTableToggle[]>([]);
  function isIntegerString(str: string): boolean {
    return /^-?\d+$/.test(str);
  }
  const sortOptions: TableSortOption[] = [
    {
      value: 'Employee ID',
      sort: (row1, row2) => {
        if (
          isIntegerString(row1.employeeId) &&
          isIntegerString(row2.employeeId)
        ) {
          return parseInt(row1.employeeId) - parseInt(row2.employeeId);
        } else {
          return row1.employeeId.localeCompare(row2.employeeId);
        }
      },
    },
    {
      value: 'First Name',
      sort: (row1, row2) => row1.firstName.localeCompare(row2.firstName),
    },
    {
      value: 'Last Name',
      sort: (row1, row2) => row1.lastName.localeCompare(row2.lastName),
    },
    {
      value: 'Email',
      sort: (row1, row2) => row1.emailAddress.localeCompare(row2.emailAddress),
    },
    {
      value: 'Mobile Number',
      sort: (row1, row2) =>
        row1?.mobileNumber?.localeCompare(row2?.mobileNumber),
    },
    {
      value: 'Created',
      sort: (row1, row2) => {
        const createdAt1 = row1.createdAt;

        const createdAt2 = row2.createdAt;

        return (
          (isDate(createdAt1) ? createdAt1 : new Date(0)).getTime() -
          (isDate(createdAt2) ? createdAt2 : new Date(0)).getTime()
        );
      },
    },
    {
      value: 'Updated',
      sort: (row1, row2) => {
        const updatedAt1 = row1.updatedAt;

        const updatedAt2 = row2.updatedAt;

        return (
          (isDate(updatedAt1) ? updatedAt1 : new Date(0)).getTime() -
          (isDate(updatedAt2) ? updatedAt2 : new Date(0)).getTime()
        );
      },
    },
    {
      value: 'Archived',
      sort: (row1, row2) => {
        const archivedDate1 = row1.archivedDate;

        const archivedDate2 = row2.archivedDate;

        return (
          (isDate(archivedDate1) ? archivedDate1 : new Date(0)).getTime() -
          (isDate(archivedDate2) ? archivedDate2 : new Date(0)).getTime()
        );
      },
    },
    {
      value: 'Department',
      sort: (row1, row2) => {
        const department1 = departmentListToString(
          company as Company,
          row1.department as string[]
        );

        const department2 = departmentListToString(
          company as Company,
          row2.department as string[]
        );

        return department1.localeCompare(department2);
      },
    },
    {
      value: 'Roles',
      sort: (row1, row2) =>
        row1.managementRoles
          .join(', ')
          .localeCompare(row2.managementRoles.join(', ')),
    },
  ];

  const [activeSort, setActiveSort] = useState<TableSortOption>(sortOptions[0]);

  const handleToggleDepartmentSelection = (department: string[]) => {
    setActiveFilters((prevFilters: ActiveTableFilterOption[]) => {
      const specificDepartmentFilter = prevFilters.find((filter) => {
        return (
          filter.parentId === 'departments' &&
          filter.key === department.join('.')
        );
      });

      if (specificDepartmentFilter) {
        // Remove the specific department filter
        return prevFilters.filter((filter) => {
          return filter.key !== specificDepartmentFilter.key;
        });
      } else {
        return [
          ...prevFilters,
          {
            parentId: 'departments',
            key: department.join('.'),
            value: department.join('.'),
            label: departmentStringToFriendlyString(
              company as Company,
              department.join('.')
            ),
            filter: (employee: any) => {
              return employeeIsPartOfOrganization(
                employee,
                department.join('.')
              );
            },
          },
        ] as ActiveTableFilterOption[];
      }
    });
  };

  if (isCompanyLoading || isEmployeesLoading) {
    return <LoadingSpinner spinnerId="employees-loading" />;
  }

  const numInvalidDepartments =
    employees?.filter(
      (employee: Employee) =>
        departmentListToString(
          company as Company,
          employee.department as string[]
        ) === 'Invalid Department'
    ).length || 0;

  return (
    <div>
      {multiplePhoneNumbers && <PhoneNumbersNotice />}
      {view === 'table' ? (
        <Table
          label={t('Employees')}
          customFilterMap={{
            departments: () => {
              return (
                <div>
                  <div className="flex items-center">
                    <h2 className="text-lg font-bold">
                      {t('Organization Tree')}
                    </h2>
                  </div>
                  {!!company && !!nextLevelKeys.length && (
                    <OrgTree
                      isSelectable
                      selectedItems={activeFilters.map((filter) => {
                        return filter.value;
                      })}
                      handleToggleSelection={handleToggleDepartmentSelection}
                      path={[company.id, 0]}
                      level={0}
                      company={company as Company}
                      completeOrgTree={company?.orgTree as OrgTreeItem}
                      data={company?.orgTree as OrgTreeItem}
                      nextLevelKeys={nextLevelKeys}
                      refetchCompany={refetchCompany}
                    />
                  )}
                </div>
              );
            },
          }}
          filters={filters}
          activeFilters={activeFilters}
          setActiveFilters={setActiveFilters}
          toggles={toggles}
          activeToggles={activeToggles}
          setActiveToggles={setActiveToggles}
          sortOptions={sortOptions.map((options) => ({
            ...options,
            value: t(options.value),
          }))}
          activeSort={{ ...activeSort, value: t(activeSort.value) }}
          setActiveSort={setActiveSort}
          actionItems={
            <Tooltip
              label={
                !((company?.orgTreeNextLevelKeys?.length || 0) > 1)
                  ? t(
                      'Company organization needs to be setup in settings before adding employees'
                    )
                  : t('Add Employee')
              }
            >
              <div>
                <Button
                  size="large"
                  type="button"
                  variant="primary"
                  onClick={() => {
                    setEmployeeSelectedForEditing(null);
                    setIsAddEmployeeModalOpen(true);
                  }}
                  className="p-[14.53px] bg-light-primary rounded-2xl justify-start items-center gap-[7.27px] flex"
                  disabled={!((company?.orgTreeNextLevelKeys?.length || 0) > 1)}
                >
                  <i className="fa-solid fa-plus text-light-primaryBackground dark:text-custom-textColor text-lg" />
                </Button>
              </div>
            </Tooltip>
          }
          headerList={[
            {
              headerLabel: t('Employee ID'),
              id: 'employeeId',
              alignment: TableCellContentAlignEnum.LEFT,
            },
            {
              headerLabel: t('First Name'),
              id: 'firstName',
              alignment: TableCellContentAlignEnum.LEFT,
            },
            {
              headerLabel: t('Last Name'),
              id: 'lastName',
              alignment: TableCellContentAlignEnum.LEFT,
            },
            {
              headerLabel: t('Email'),
              id: 'emailAddress',
              alignment: TableCellContentAlignEnum.LEFT,
            },
            {
              headerLabel: multiplePhoneNumbers ? t('Phone') : t('Mobile Number'),
              id: 'mobileNumber',
              alignment: TableCellContentAlignEnum.LEFT,
            },
            {
              headerLabel: t('Created'),
              id: 'createdAt',
              alignment: TableCellContentAlignEnum.LEFT,
            },
            {
              headerLabel: t('Updated'),
              id: 'updatedAt',
              alignment: TableCellContentAlignEnum.LEFT,
            },
            {
              headerLabel: t('Archived'),
              id: 'archivedDate',
              alignment: TableCellContentAlignEnum.LEFT,
            },
            {
              headerLabel: (
                <div>
                  {t('Department')}
                  {numInvalidDepartments > 0 ? (
                    <span className="text-semantic-error">
                      {' '}
                      {t('num-invalid', { numInvalid: numInvalidDepartments })}
                    </span>
                  ) : null}
                </div>
              ),
              id: 'department',
              alignment: TableCellContentAlignEnum.LEFT,
            },
            {
              headerLabel: t('Roles'),
              id: 'managementRoles',
              alignment: TableCellContentAlignEnum.CENTER,
            },
            {
              headerLabel: t('Actions'),
              id: 'actions',
              alignment: TableCellContentAlignEnum.RIGHT,
            },
          ]}
          dataList={employees as unknown as BasicTableDataRow[]}
          mapData={(employee, index) => {
            const department = departmentListToString(
              company as Company,
              employee.department as string[]
            );

            const createdAt = employee.createdAt;
            const updatedAt = employee.updatedAt;
            const archivedDate = employee.archivedDate;

            const employeeHasBeenArchivedForAtLeast1Month =
              employee.archivedDate &&
              isBefore(addMonths(employee.archivedDate, 1), new Date());

            return {
              id: employee.id,
              isDeleted: !!employee.archivedDate,
              index,
              employeeId: employee.employeeId,
              firstName: employee.firstName,
              lastName: employee.lastName,
              emailAddress: employee.emailBounce ? (
                /**
                 * The address is shown as it always was, with a warning beside
                 * it. Mirrors the invalid-department cell below.
                 *
                 * The tooltip carries the provider's own wording, which is
                 * third-party text that can echo addresses and internal
                 * hostnames — so it is rendered as text and never as markup.
                 * It is deliberately not said which survey exposed the bounce:
                 * that is a fact about who was surveyed, and on an anonymous
                 * survey it would be a way back to a person.
                 */
                <Tooltip
                  label={
                    employee.emailBounce.reason
                      ? t('Email could not be delivered: {{reason}}', {
                          reason: employee.emailBounce.reason,
                        })
                      : t('Email could not be delivered to this address.')
                  }
                >
                  <span className="inline-flex items-center gap-1">
                    <span>{employee.emailAddress}</span>
                    <i
                      className="fa-solid fa-triangle-exclamation text-semantic-error"
                      aria-label={t('Email could not be delivered')}
                    />
                  </span>
                </Tooltip>
              ) : (
                employee.emailAddress
              ),
              mobileNumber: multiplePhoneNumbers
                ? phoneCell(employee.phoneNumbers ?? [])
                : employee.mobileNumber,
              department: (
                <span
                  className={classNames({
                    'text-semantic-error': department === 'Invalid Department',
                  })}
                >
                  {department}
                </span>
              ),
              createdAt: createdAt
                ? t('dateShort', { date: new Date(createdAt) })
                : '',
              updatedAt: updatedAt
                ? t('dateShort', { date: new Date(updatedAt) })
                : '',
              archivedDate: employee.archivedDate
                ? t('dateShort', { date: new Date(archivedDate) })
                : '',
              managementRoles: employee.managementRoles
                .map((role: ManagementRole) => role.name)
                .join(', '),
              actions: (
                <div className="flex justify-end space-x-1">
                  <Tooltip label={t('Edit')}>
                    <button
                      onClick={() => {
                        setEmployeeSelectedForEditing(employee);
                        setIsAddEmployeeModalOpen(true);
                      }}
                    >
                      <i className="fa-solid fa-pen-to-square text-light-primaryText dark:text-dark-primaryText hover:text-blue-500 hover:dark:text-blue-500" />
                    </button>
                  </Tooltip>
                  {!employee.archivedDate && (
                    <Tooltip label={t('Archive Employee')}>
                      <button
                        onClick={() => {
                          setIsArchiveEmployeeConfirmationModalOpen(true);
                          setEmployeeSelectedForArchiving(employee);
                        }}
                      >
                        <i className="fa-solid fa-box-archive text-light-primaryText dark:text-dark-primaryText hover:text-blue-500 hover:dark:text-blue-500" />
                      </button>
                    </Tooltip>
                  )}
                  {employeeHasBeenArchivedForAtLeast1Month && (
                    <Tooltip label={t('Delete Employee')}>
                      <button
                        onClick={() => {
                          setIsDeleteEmployeeConfirmationModalOpen(true);
                          setEmployeeSelectedForDeletion(employee);
                        }}
                      >
                        <i className="fa-solid fa-trash text-light-primaryText dark:text-dark-primaryText hover:text-blue-500 hover:dark:text-blue-500" />
                      </button>
                    </Tooltip>
                  )}
                </div>
              ),
            };
          }}
        />
      ) : (
        <EmployeeOrgChart />
      )}
      <AddEmployeeModal
        open={isAddEmployeeModalOpen}
        setOpen={setIsAddEmployeeModalOpen}
        employeeSelectedForEditing={employeeSelectedForEditing}
        employeeSelectedForEditingHasValidDepartment={
          employeeSelectedForEditing
            ? departmentListToString(
                company as Company,
                employeeSelectedForEditing.department as string[]
              ) !== 'Invalid Department'
            : false
        }
        refetchEmployeeList={refetchEmployees}
      />
      <ConfirmationModal
        text={t('archive-confirmation', {
          firstName: employeeSelectedForArchiving?.firstName,
          lastName: employeeSelectedForArchiving?.lastName,
        })}
        open={isArchiveEmployeeConfirmationModalOpen}
        setOpen={setIsArchiveEmployeeConfirmationModalOpen}
        onConfirm={onConfirmArchiveEmployee}
        onCancel={() => {
          setIsArchiveEmployeeConfirmationModalOpen(false);
        }}
      />
      <ConfirmationModal
        text={t('delete-confirmation', {
          firstName: employeeSelectedForDeletion?.firstName,
          lastName: employeeSelectedForDeletion?.lastName,
        })}
        open={isDeleteEmployeeConfirmationModalOpen}
        setOpen={setIsDeleteEmployeeConfirmationModalOpen}
        onConfirm={onConfirmDeleteEmployee}
        onCancel={() => {
          setIsDeleteEmployeeConfirmationModalOpen(false);
        }}
      />
    </div>
  );
}

export default Employees;
